/**
 * Auto Premium Breakdown Utilities
 *
 * The per-vehicle price for an auto offer or policy, shared by every surface that shows one:
 * staff's Premium Breakdown modal, the policy breakdown PDF, the account Price Breakdown, quote's
 * review page and the offer PDF. Each surface keeps only its layout.
 *
 * Rule (#35976): a car costs its own premium plus an even share of the shared costs; a trailer
 * costs only its own premium; shared costs (total minus every car's and trailer's own premium)
 * are split across cars only, to the cent, so the rows add up to the total.
 *
 * Related GitHub issues: #29852 (per_car_total), #32269 (account parity), #35976 (one calculation)
 */

import { umUimCoverageArray } from '@ourbranch/lookups';

/**
 * A single coverage line for a vehicle, as parsed out of a ClarionDoor premium breakdown.
 */
export type PremiumRate = { type: string; amount: number };

/**
 * A raw ClarionDoor premium breakdown map (`it1-COLL`, `it1-per_car_total`, `BI`, ...).
 * Stored records are not guaranteed to hold numbers on every key, so nulls are tolerated.
 */
export type PremiumBreakdownMap = Record<string, number | null | undefined>;

/**
 * Options for the legacy fallback used when a record predates `per_car_total`.
 */
export type CarTotalOptions = {
  /** Whether to drop UM/UIM types from the fallback sum (legacy double-count guard) */
  excludeUmUim?: boolean;
  /** Set of UM/UIM coverage type strings to drop */
  umUimTypes?: Set<string>;
};

/**
 * Extract one car's coverage lines from a raw premium breakdown map.
 *
 * Keys are matched case-insensitively (`it1-`, `IT1-`, ...) and types are normalized to
 * upper case, so `it1-per_car_total` becomes `{ type: 'PER_CAR_TOTAL', amount }`.
 *
 * @param premiumBreakdown - Raw premium breakdown object from ClarionDoor
 * @param carIteration - The car's iteration number (1-indexed)
 * @returns The coverage rates filed under that iteration
 */
export function getCarRatesFromBreakdown(premiumBreakdown: PremiumBreakdownMap, carIteration: number): PremiumRate[] {
  const iterationPrefix = `it${carIteration}-`;
  return Object.entries(premiumBreakdown)
    .filter(([key]) => key.toLowerCase().startsWith(iterationPrefix))
    .map(([key, value]) => ({
      type: key.replace(/^it\d+-/i, '').toUpperCase(),
      amount: typeof value === 'number' ? value : 0
    }));
}

/**
 * The single source of truth for "what does this car's own coverage cost".
 *
 * Uses the rater's `PER_CAR_TOTAL` when the record has one. Policy-level lines such as UM/UIM
 * are filed under a car's iteration but sit outside `per_car_total`, so summing the iteration's
 * line items instead would charge them to that one car (#32269). Records written before
 * `per_car_total` was stored (#29852) fall back to that sum, optionally dropping the UM/UIM
 * types that double-count on those legacy records.
 *
 * `getAutoVehiclePrices` resolves every car's base through here.
 *
 * @param rates - Array of coverage rates for the vehicle
 * @param options - Optional configuration for legacy fallback behavior
 * @returns The car's own coverage premium; callers add any shared-cost split themselves
 */
export function resolveCarTotalFromRates(rates: PremiumRate[], options?: CarTotalOptions): number {
  const perCarTotalRate = rates.find((rate) => rate.type?.toUpperCase() === 'PER_CAR_TOTAL');
  if (perCarTotalRate) {
    return perCarTotalRate.amount;
  }

  const { excludeUmUim = false, umUimTypes } = options || {};
  return rates
    .filter((rate) => !(excludeUmUim && umUimTypes?.has(rate.type)))
    .reduce((sum, rate) => sum + (typeof rate.amount === 'number' ? rate.amount : 0), 0);
}

/**
 * A coverage line on an offer option's `autoCoverages`.
 */
export type AutoCoverageRate = { type?: string | null; amount?: number | null; iteration?: number | string | null };

/**
 * Reshape an offer option's `autoCoverages` into the `it{N}-{type}` map a bound policy stores,
 * so offers and policies are priced by the same code. Lines without an iteration are policy-level
 * and are not part of any vehicle's own premium, so they are left out.
 *
 * @param autoCoverages - The selected option's `autoCoverages`
 * @returns A premium breakdown map keyed by iteration
 */
export function premiumBreakdownFromAutoCoverages(
  autoCoverages: AutoCoverageRate[] | null | undefined
): PremiumBreakdownMap {
  return (autoCoverages ?? []).reduce<Record<string, number>>((premiumBreakdown, { type, amount, iteration }) => {
    if (!type || !iteration) {
      return premiumBreakdown;
    }
    const key = `it${iteration}-${type}`;
    premiumBreakdown[key] = (premiumBreakdown[key] ?? 0) + (amount ?? 0);
    return premiumBreakdown;
  }, {});
}

/** Coverage types a bound policy stores per trailer; their sum is the rater's `per_trailer_total`. */
const TRAILER_COVERAGE_TYPES = new Set(['COMP_TRLR', 'COLL_TRLR', 'CONTENTS']);

/**
 * A renters endorsement's premium. ClarionDoor files it under car 1's iteration, but it is part of
 * the auto total and a shared cost, so it is never any car's own premium.
 */
const RENTERS_ENDORSEMENT_TYPE = 'RENTERS_TOTAL';

const toCents = (amount: number): number => Math.round(amount * 100);

/**
 * Whether a record from before `per_car_total` was stored (#29852) over-counts the cars by the
 * UM/UIM line ClarionDoor files under a single car's iteration. Only provable when the record
 * also stores the `car` premium to compare against.
 */
function isLegacyUmUimDoubleCounted(premiumBreakdown: PremiumBreakdownMap, carRates: PremiumRate[][]): boolean {
  const hasPerCarTotal = carRates.some((rates) => rates.some((rate) => rate.type === 'PER_CAR_TOTAL'));
  const storedCarPremium = premiumBreakdown.car;
  if (hasPerCarTotal || typeof storedCarPremium !== 'number') {
    return false;
  }
  const carLineItemTotal = carRates.flat().reduce((sum, rate) => sum + rate.amount, 0);
  return carLineItemTotal > storedCarPremium;
}

export type VehiclePrices = {
  cars: { ownPremium: number; sharedCosts: number; total: number }[];
  trailers: { total: number }[];
};

/**
 * Price every car and trailer on an auto offer option or policy. Cars come first in iteration
 * order (car 1 is `it1-`), then trailers, the same sequence the rater numbers them in.
 *
 * @param premiumBreakdown - A policy's `premiumBreakdown`, or an offer option's
 *   `autoCoverages` passed through `premiumBreakdownFromAutoCoverages`
 * @param carCount - Number of cars
 * @param trailerCount - Number of trailers
 * @param totalPrice - What the customer pays for the term (offer `autoTotal`, or a policy's
 *   premium + surplus contribution + fees)
 * @param state - Policy state, used to recognise UM/UIM lines on legacy records
 * @returns Dollar amounts rounded to cents; the car and trailer totals add up to `totalPrice`
 */
export function getAutoVehiclePrices({
  premiumBreakdown,
  carCount,
  trailerCount,
  totalPrice,
  state
}: {
  premiumBreakdown: PremiumBreakdownMap | null | undefined;
  carCount: number;
  trailerCount: number;
  totalPrice: number;
  state?: string;
}): VehiclePrices {
  if (carCount <= 0) {
    return { cars: [], trailers: [] };
  }
  const breakdown = premiumBreakdown ?? {};

  const carRates = Array.from({ length: carCount }, (_, index) =>
    getCarRatesFromBreakdown(breakdown, index + 1).filter((rate) => rate.type !== RENTERS_ENDORSEMENT_TYPE)
  );
  const umUimTypes = new Set([
    ...((state && umUimCoverageArray[state as keyof typeof umUimCoverageArray]) || []),
    'UM_UIM'
  ]);
  const excludeUmUim = isLegacyUmUimDoubleCounted(breakdown, carRates);
  const carOwnCents = carRates.map((rates) => toCents(resolveCarTotalFromRates(rates, { excludeUmUim, umUimTypes })));

  const trailerCents = Array.from({ length: trailerCount }, (_, index) => {
    const rates = getCarRatesFromBreakdown(breakdown, carCount + index + 1);
    const perTrailerTotal = rates.find((rate) => rate.type === 'PER_TRAILER_TOTAL');
    if (perTrailerTotal) {
      return toCents(perTrailerTotal.amount);
    }
    return toCents(
      rates.filter((rate) => TRAILER_COVERAGE_TYPES.has(rate.type)).reduce((sum, rate) => sum + rate.amount, 0)
    );
  });

  const sumCents = (cents: number[]) => cents.reduce((sum, amount) => sum + amount, 0);
  const sharedCents = toCents(totalPrice) - sumCents(carOwnCents) - sumCents(trailerCents);
  const sharedCentsPerCar = Math.floor(sharedCents / carCount);
  const leftoverCents = sharedCents - sharedCentsPerCar * carCount;

  return {
    cars: carOwnCents.map((ownCents, index) => {
      const shareCents = sharedCentsPerCar + (index < leftoverCents ? 1 : 0);
      return { ownPremium: ownCents / 100, sharedCosts: shareCents / 100, total: (ownCents + shareCents) / 100 };
    }),
    trailers: trailerCents.map((cents) => ({ total: cents / 100 }))
  };
}
